import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Bell, TrendingUp, TrendingDown, Settings, Unplug } from 'lucide-react';
import axios from 'axios';
import config from '../config';
import { variationColor } from '../utils/variationUtils';
import PopinAlertes from './alertes/PopinAlertes';
import {
  LIBELLES_METRIQUE as LIBELLES, LIBELLES_REGIE, LIBELLES_STATUT_ACCES,
  formatValeur, formatEcart, depuis, titreAlerte, corpsAlerte, estAlerteAcces,
  destinationAlerte
} from './alertes/messageAlerte';

const API_URL = config.API_URL;

/** Le balayage serveur est horaire : interroger plus souvent n'apporterait rien. */
const INTERVALLE_POLL = 5 * 60 * 1000;

/** L'onglet est-il sous les yeux de l'utilisateur ? Décide du visage que prend l'alerte. */
const ongletVisible = () => (
  typeof document === 'undefined' || document.visibilityState === 'visible'
);

/**
 * Pastille de notifications dans l'en-tête.
 *
 * Montée dans l'en-tête et non dans l'écran Notifications : elle doit être visible quelle
 * que soit la vue, sans quoi une alerte ne se signalerait que sur la page de ses réglages.
 *
 * Le panneau se déploie tout seul à l'arrivée d'une alerte — mais jamais au chargement de
 * la page, sinon il s'ouvrirait à chaque navigation tant qu'une alerte reste ouverte.
 *
 * Deux canaux se partagent l'écran, chacun nommé par le moment où il agit :
 *
 * | Onglet | Ce qui s'affiche |
 * |---|---|
 * | visible | la **pop-in**, qui ne demande aucune autorisation |
 * | masqué  | la **notification système**, si le navigateur l'autorise |
 *
 * Ils ne se déclenchent jamais ensemble : une bulle du système devant quelqu'un qui regarde
 * la page n'apporte rien. Ce partage a une raison — la bulle exigeait une autorisation même
 * pour prévenir quelqu'un qui avait Kokpit sous les yeux, et un utilisateur ayant refusé une
 * fois ne recevait plus rien, en silence. L'autorisation ne commande plus que le cas où la
 * page n'est pas regardée, le seul que la pop-in ne peut pas couvrir.
 *
 * Une alerte survenue onglet masqué sans que la bulle ait pu partir ne s'affiche nulle part :
 * elle est mise en attente et présentée au retour. Sans ce rattrapage, la pop-in ne servirait
 * qu'à ceux qui n'en avaient pas besoin.
 *
 * Ni service worker ni Web Push dans le projet : navigateur fermé, seul l'e-mail prévient.
 */
const AlertNotificationsBell = ({ token, onOpenSettings, onOuvrirCompte }) => {
  const [alertes, setAlertes] = useState([]);
  const [actif, setActif] = useState(false);
  const [ouvert, setOuvert] = useState(false);
  const [popin, setPopin] = useState([]);
  const [visible, setVisible] = useState(ongletVisible);

  // Photo des alertes non lues au moment où le panneau s'ouvre : sert à les mettre en avant
  // alors même qu'elles viennent d'être acquittées.
  const nonLuesALOuverture = useRef(new Set());

  // Alertes survenues onglet masqué sans autorisation : personne ne les a vues passer, on les
  // présente au retour. Une `ref` et non un state — elles ne s'affichent pas encore.
  const enAttente = useRef([]);

  // Identifiants connus au passage précédent : sert à distinguer une alerte qui vient
  // d'arriver d'une alerte déjà présente.
  const connues = useRef(new Set());
  const premierChargement = useRef(true);
  const conteneur = useRef(null);

  /**
   * Acquitter des alertes : « vues par l'utilisateur », et non « affichées ».
   *
   * La nuance porte toute la pastille. Une carte apparue pendant que personne ne regardait
   * l'écran n'acquitte rien — c'est même le cas qu'il faut signaler au retour. Seuls trois
   * gestes acquittent : fermer une carte, ouvrir le panneau, et le consulter.
   *
   * L'état local est mis à jour sans attendre le serveur : la pastille doit répondre au
   * clic, pas au réseau. Un échec se rattrape au sondage suivant.
   */
  const acquitter = useCallback((ids) => {
    if (!ids || ids.length === 0) return;
    const maintenant = new Date().toISOString();
    setAlertes(actuelles => actuelles.map(a => (
      ids.includes(a.id) ? { ...a, seenAt: a.seenAt || maintenant } : a
    )));
    axios.put(`${API_URL}/api/alerts/seen`, { ids }, {
      headers: { Authorization: `Bearer ${token}` }
    }).catch(() => {});
  }, [token]);

  const interroger = useCallback(async () => {
    if (!token) return;

    let data;
    try {
      const reponse = await axios.get(`${API_URL}/api/alerts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      data = reponse.data;
    } catch {
      // Réseau indisponible ou session expirée : on réessaiera au prochain passage.
      return;
    }
    if (!data?.success) return;

    const ouvertes = data.open || [];
    setAlertes(ouvertes);
    setActif(Boolean(data.isActive));

    // Une alerte refermée entre deux sondages retire sa carte : la laisser affirmerait un
    // dépassement qui est retombé.
    const idsOuverts = new Set(ouvertes.map(a => a.id));
    setPopin(actuelles => actuelles.filter(a => idsOuverts.has(a.id)));

    const nouvelles = ouvertes.filter(a => !connues.current.has(a.id));
    ouvertes.forEach(a => connues.current.add(a.id));

    // Au premier passage, on se contente d'enregistrer l'existant : déployer le panneau
    // à chaque chargement de page serait insupportable.
    if (premierChargement.current) {
      premierChargement.current = false;
      return;
    }
    if (nouvelles.length === 0) return;

    // Les canaux sont figés sur l'alerte à son ouverture, d'après les réglages du compte.
    const visible = ongletVisible();
    const bulleDisponible = typeof window !== 'undefined' && 'Notification' in window
      && Notification.permission === 'granted';

    const cartes = [];
    const differees = [];

    for (const alerte of nouvelles) {
      if (alerte.seenAt) continue;

      if (visible) {
        // La bulle système ne sert à rien devant quelqu'un qui regarde la page ; si la carte
        // est refusée, c'est que l'utilisateur ne veut pas être interrompu pendant son travail.
        if (alerte.notifyInPopin) cartes.push(alerte);
        continue;
      }

      if (alerte.notifyInBrowser && bulleDisponible) {
        // La bulle ne vaut pas acquittement : partie pendant une absence, personne ne l'a
        // forcément vue. La pastille reste rouge jusqu'à ce que l'utilisateur y touche.
        new Notification(titreAlerte(alerte), { body: corpsAlerte(alerte), tag: alerte.id });
        continue;
      }

      // Rien n'a pu s'afficher : la carte prendra le relais au retour sur l'onglet.
      if (alerte.notifyInPopin) differees.push(alerte);
    }

    if (cartes.length > 0) setPopin(actuelles => [...cartes, ...actuelles]);
    if (differees.length > 0) enAttente.current = [...differees, ...enAttente.current];

    // Aucune carte à montrer, ni maintenant ni au retour : le panneau reste le seul signal
    // in-app — que la bulle soit partie pendant l'absence ou que tout soit décoché. Le
    // déployer par-dessus une carte dirait deux fois la même chose, et la recouvrirait.
    if (cartes.length === 0 && differees.length === 0) setOuvert(true);
  }, [token]);

  /**
   * Présenter au retour ce qui n'a pas pu l'être pendant l'absence.
   *
   * Débloquer la file au retour sur l'onglet est ce qui rend la pop-in utile à ceux dont le
   * navigateur refuse les bulles : sans cela, ils resteraient exactement où ils étaient.
   */
  useEffect(() => {
    const auChangement = () => {
      setVisible(ongletVisible());
      if (!ongletVisible() || enAttente.current.length === 0) return;
      const rattrapees = enAttente.current;
      enAttente.current = [];
      setPopin(actuelles => [...rattrapees, ...actuelles]);
    };
    document.addEventListener('visibilitychange', auChangement);
    return () => document.removeEventListener('visibilitychange', auChangement);
  }, []);

  /**
   * Consulter le panneau acquitte ce qu'il montre — mais seulement si quelqu'un le regarde.
   *
   * Le panneau se déploie aussi tout seul, y compris onglet masqué après l'envoi d'une
   * bulle : acquitter là serait effacer la pastille sans que personne n'ait rien vu.
   */
  useEffect(() => {
    if (!ouvert || !visible) return;
    const nonLues = alertes.filter(a => !a.seenAt).map(a => a.id);
    if (nonLues.length > 0) acquitter(nonLues);
  }, [ouvert, visible, alertes, acquitter]);

  /**
   * Ce qui reste marqué « nouveau » tant que le panneau est ouvert.
   *
   * Sans cette mémoire, l'acquittement ci-dessus effacerait la mise en avant dans le même
   * souffle : le panneau s'ouvrirait sur des alertes déjà grisées, et « nouveau » ne se
   * verrait jamais.
   *
   * Elle s'enrichit au lieu de se figer, sinon une alerte arrivée pendant que le panneau
   * est ouvert serait acquittée sans jamais avoir été signalée. Déclaré **après** l'effet
   * d'acquittement : les deux lisent le même rendu, celui où l'alerte est encore non lue.
   */
  useEffect(() => {
    if (!ouvert) { nonLuesALOuverture.current = new Set(); return; }
    alertes.filter(a => !a.seenAt).forEach(a => nonLuesALOuverture.current.add(a.id));
  }, [ouvert, alertes]);

  useEffect(() => {
    if (!token) return undefined;
    interroger();
    const timer = setInterval(interroger, INTERVALLE_POLL);
    return () => clearInterval(timer);
  }, [token, interroger]);

  // Fermeture au clic ailleurs, comme les autres menus de l'en-tête
  useEffect(() => {
    if (!ouvert) return undefined;
    const auClic = (e) => {
      if (conteneur.current && !conteneur.current.contains(e.target)) setOuvert(false);
    };
    document.addEventListener('mousedown', auClic);
    return () => document.removeEventListener('mousedown', auClic);
  }, [ouvert]);

  // Nombre d'alertes que l'utilisateur n'a pas encore acquittées : c'est ce que la pastille
  // annonce. Le compte des alertes ouvertes resterait rouge indéfiniment sur un dépassement
  // qui dure, et la pastille cesserait de vouloir dire « il y a du nouveau ».
  const nonLues = alertes.filter(a => !a.seenAt).length;

  // Rien à signaler et outil jamais activé : inutile d'encombrer l'en-tête d'une cloche
  // qui ne servira jamais.
  if (!token || (!actif && alertes.length === 0)) return null;

  return (
    <>
    <PopinAlertes
      alertes={popin}
      onFermer={(id) => setPopin(actuelles => actuelles.filter(a => a.id !== id))}
      onToutVoir={() => { setPopin([]); setOuvert(true); }}
      onOuvrir={onOuvrirCompte ? (alerte, dashboard) => {
        /**
         * Ouvrir depuis la carte retire la carte.
         *
         * La laisser flotter au-dessus du dashboard qu'elle vient d'ouvrir n'aurait plus
         * d'objet : on est arrivé là où elle menait. C'est aussi, comme la fermeture, un geste
         * qui prouve qu'elle a été vue — d'où l'acquittement.
         */
        setPopin(actuelles => actuelles.filter(a => a.id !== alerte.id));
        if (!alerte.seenAt) acquitter([alerte.id]);
        onOuvrirCompte(dashboard, alerte.cible, alerte);
      } : undefined}
    />
    <div ref={conteneur} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOuvert(!ouvert)}
        title={nonLues > 0
          ? `${nonLues} nouvelle${nonLues > 1 ? 's' : ''} alerte${nonLues > 1 ? 's' : ''}`
          : alertes.length > 0 ? `${alertes.length} alerte(s) en cours` : 'Aucune alerte'}
        style={{
          position: 'relative', background: 'none', border: 'none', cursor: 'pointer',
          display: 'flex', alignItems: 'center', padding: '6px', color: '#374151'
        }}
      >
        <Bell size={20} />
        {nonLues > 0 && (
          <span style={{
            position: 'absolute', top: '0', right: '0',
            minWidth: '17px', height: '17px', padding: '0 4px',
            borderRadius: '9px', background: '#ef4444', color: 'white',
            fontSize: '10px', fontWeight: 700, lineHeight: '17px', textAlign: 'center'
          }}>
            {nonLues > 9 ? '9+' : nonLues}
          </span>
        )}
      </button>

      {ouvert && (
        <div style={{
          position: 'absolute', top: '100%', right: 0, marginTop: '8px',
          width: '380px', maxWidth: 'calc(100vw - 32px)',
          background: 'white', border: '1px solid #e5e7eb', borderRadius: '8px',
          boxShadow: '0 10px 25px rgba(0,0,0,0.15)', zIndex: 1100, overflow: 'hidden'
        }}>
          <div style={{
            padding: '12px 14px', borderBottom: '1px solid #f3f4f6',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between'
          }}>
            {/* « Alertes » et non « Alertes de trafic » : le panneau porte aussi les
                comptes devenus inaccessibles. */}
            <strong style={{ fontSize: '13px', color: '#111827' }}>
              Alertes{alertes.length > 0 ? ` (${alertes.length})` : ''}
            </strong>
            <button
              type="button"
              onClick={() => { setOuvert(false); onOpenSettings?.(); }}
              title="Régler les alertes"
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                color: '#6b7280', display: 'flex', padding: '2px'
              }}
            >
              <Settings size={16} />
            </button>
          </div>

          {/* `textAlign` explicite : la cloche est montée dans l'en-tête, dont un ancêtre
              centre son texte. Les lignes d'alerte en héritaient et s'affichaient centrées,
              nom du compte comme détail — seule la pastille de pourcentage, alignée par le
              flex, paraissait à sa place. */}
          <div style={{ maxHeight: '340px', overflowY: 'auto', textAlign: 'left' }}>
            {alertes.length === 0 ? (
              <div style={{ padding: '20px 14px', fontSize: '13px', color: '#6b7280', textAlign: 'center' }}>
                Aucune alerte en cours.
              </div>
            ) : alertes.map((alerte, index) => {
              const acces = estAlerteAcces(alerte);
              const monte = alerte.direction === 'up';
              // Un accès coupé n'a pas de sens de variation : rouge, sans nuance.
              const Fleche = acces ? Unplug : (monte ? TrendingUp : TrendingDown);
              // Une hausse de dépense ou de clics est mauvaise : sémantique inversée
              const couleur = acces ? '#ef4444' : variationColor(alerte.deviationPercent, true);
              // Où mène l'alerte : la règle est partagée avec la carte en bas à droite, dans
              // `alertes/messageAlerte.js`.
              const { dashboard, ouvrable: meneQuelquePart, titre } = destinationAlerte(alerte);
              const ouvrable = Boolean(onOuvrirCompte && meneQuelquePart);

              return (
                <div
                  key={alerte.id}
                  onClick={ouvrable ? () => { setOuvert(false); onOuvrirCompte(dashboard, alerte.cible, alerte); } : undefined}
                  title={ouvrable ? titre : undefined}
                  onMouseEnter={e => { if (ouvrable) e.currentTarget.style.backgroundColor = '#f9fafb'; }}
                  onMouseLeave={e => { if (ouvrable) e.currentTarget.style.backgroundColor = 'transparent'; }}
                  style={{
                    display: 'flex', alignItems: 'flex-start', gap: '10px',
                    padding: '10px 14px', fontSize: '13px',
                    borderTop: index === 0 ? 'none' : '1px solid #f9fafb',
                    cursor: ouvrable ? 'pointer' : 'default',
                    backgroundColor: 'transparent', transition: 'background-color 0.12s ease'
                  }}
                >
                  <Fleche size={16} style={{ color: couleur, flexShrink: 0, marginTop: '2px' }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontWeight: 500, color: '#111827',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                    }}>
                      {alerte.accountName}
                    </div>
                    <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '2px' }}>
                      {acces ? (
                        <>
                          {LIBELLES_REGIE[alerte.provider] || alerte.provider} ·{' '}
                          {LIBELLES_STATUT_ACCES[alerte.status] || alerte.status}
                        </>
                      ) : (
                        <>
                          {LIBELLES[alerte.metric] || alerte.metric} ·{' '}
                          {formatValeur(alerte.metric, alerte.windowValue)} sur 24 h contre{' '}
                          {formatValeur(alerte.metric, alerte.baselineValue)} en moyenne
                        </>
                      )}
                    </div>
                    <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '2px' }}>
                      {depuis(alerte.openedAt)}
                    </div>
                  </div>
                  {/* Une alerte d'accès n'a pas d'écart à afficher : le statut le dit déjà
                      sur la ligne du dessus, et un « — » n'apprendrait rien. */}
                  {!acces && (
                    <div style={{ fontWeight: 700, color: couleur, flexShrink: 0 }}>
                      {formatEcart(alerte.deviationPercent)}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
    </>
  );
};

export default AlertNotificationsBell;
